import React, { useEffect, useState } from 'react';
import {
    Box,
    TextField,
    IconButton,
    Tooltip,
    Stack,
    Divider,
    Grid2,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import CentInput from '../../../../components/atom/CentInput';

import { getTicketValidationWarnings, updatePriceFromMatrix } from './ticketValidationHelpers';
import { formatCentsToUsd } from '../../../../helpers/formatCentsToUsd';
import { previewItemFinalPriceCents } from '../../../../helpers/calculateShowFees';

function OneTicketEdit({
    values,
    setFieldValue,
    isSubmitting,
    errors = {},
    addAddedTicket = () => {},
    removeAddedTicket = () => {},
    closePopup = () => {},
    limitedModification,
    showFees,
    isOnline,
    allTicketTypes = [],
    allTicketOptions = [],
    priceOverrides,
    isTicketCombinationAllowed = () => true,
    netRate = false,
    commissionMatrix = {},
}) {
    const [popupValue, setPopupValue] = useState(values);
    const setFieldAndPopupValue = (field, value) => {
        setFieldValue(field, value);
        setPopupValue((old) => ({ ...old, [field]: value }));
    };
    useEffect(() => {
        setPopupValue(values);
    }, [values]);
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [validationWarnings, setValidationWarnings] = useState({
        seatMismatch: false,
        invalidCombination: false,
        messages: [],
    });

    // Get current ticket type and option name
    const currentTicketType = allTicketTypes?.find((type) => type.id === popupValue?.ticketTypeId);
    const currentTicketOptionName = popupValue?.ticketOptionName;
    const allowedOptionsForSelectedType = allTicketOptions?.filter((option) => {
        if (!popupValue?.ticketTypeId) {
            return true;
        }
        return isTicketCombinationAllowed(popupValue.ticketTypeId, option.id);
    });

    // Validate warnings when seat or type/option changes
    useEffect(() => {
        if (!popupValue?.ticketTypeId || !popupValue?.ticketOptionId) {
            setValidationWarnings({ seatMismatch: false, invalidCombination: false, messages: [] });
            return;
        }

        // Get validation warnings
        const warnings = getTicketValidationWarnings(
            popupValue,
            currentTicketType,
            currentTicketOptionName,
            t,
        );
        setValidationWarnings(warnings);
    }, [
        popupValue?.ticketTypeId,
        popupValue?.ticketOptionId,
        popupValue?.ticketSeatId,
        currentTicketType,
        currentTicketOptionName,
    ]);

    const finalPrice = previewItemFinalPriceCents(popupValue, {
        netRate,
        commissionMatrix,
        showFees,
        isOnline,
    });

    return (
        <Box
            sx={{
                p: 2,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                borderTopLeftRadius: 0,
                maxWidth: 500,
            }}
        >
            <Box
                style={{
                    position: 'absolute',
                    top: '-28px',
                    left: 0,
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    borderRadius: '4px 4px 0 0',
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    borderBottomColor: 'transparent',
                    borderWidth: '1px 1px 0 1px',
                    borderStyle: 'solid',
                    height: '30px',
                    minWidth: '30%',
                }}
            >
                <h4
                    style={{
                        padding: '4px 8px',
                        margin: 0,
                        fontSize: '16px',
                        textDecoration: popupValue?.shouldDelete ? 'line-through' : 'none',
                    }}
                >
                    {popupValue?.ticketSeatName || ''}
                </h4>
            </Box>

            <Grid2 container>
                {/* Main ticket fields */}
                <Grid2 item size={6}>
                    {/* Ticket Option */}
                    <FormControl fullWidth required>
                        <InputLabel>{t('bookingitems.columns.ticketOptionName')}</InputLabel>
                        <Select
                            value={popupValue?.ticketOptionId || ''}
                            onChange={(ev) => {
                                const selectedOption = allTicketOptions?.find(
                                    (opt) => opt.id === ev.target.value,
                                );
                                const optionName = selectedOption?.name || null;
                                setFieldAndPopupValue('ticketOptionId', ev.target.value);
                                setFieldAndPopupValue('ticketOptionName', optionName);
                                // Update price when option changes
                                updatePriceFromMatrix(
                                    popupValue?.ticketTypeId,
                                    optionName,
                                    allTicketTypes,
                                    setFieldAndPopupValue,
                                    ev.target.value,
                                    priceOverrides,
                                );
                            }}
                            disabled={isSubmitting || limitedModification}
                            label={t('bookingitems.columns.ticketOptionName')}
                        >
                            {allowedOptionsForSelectedType?.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid2>
                <Grid2 item size={6}>
                    {/* Ticket Type */}
                    <FormControl fullWidth required>
                        <InputLabel>{t('bookingitems.columns.ticketTypeName')}</InputLabel>
                        <Select
                            value={popupValue?.ticketTypeId || ''}
                            onChange={(ev) => {
                                const selectedType = allTicketTypes?.find(
                                    (type) => type.id === ev.target.value,
                                );
                                setFieldAndPopupValue('ticketTypeId', ev.target.value);
                                setFieldAndPopupValue('ticketTypeName', selectedType?.name || null);
                                if (
                                    popupValue?.ticketOptionId &&
                                    !isTicketCombinationAllowed(
                                        ev.target.value,
                                        popupValue.ticketOptionId,
                                    )
                                ) {
                                    setFieldAndPopupValue('ticketOptionId', '');
                                    setFieldAndPopupValue('ticketOptionName', '');
                                }
                                // Update price when type changes
                                updatePriceFromMatrix(
                                    ev.target.value,
                                    popupValue?.ticketOptionName,
                                    allTicketTypes,
                                    setFieldAndPopupValue,
                                    popupValue?.ticketOptionId,
                                    priceOverrides,
                                );
                            }}
                            disabled={isSubmitting || limitedModification}
                            label={t('bookingitems.columns.ticketTypeName')}
                        >
                            {allTicketTypes
                                ?.filter((type) =>
                                    type.seats?.some(
                                        (seat) => seat.id === popupValue?.ticketSeatId,
                                    ),
                                )
                                ?.map((type) => (
                                    <MenuItem key={type.id} value={type.id}>
                                        {type.name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Grid2>
                <Grid2 item size={6}>
                    {/* Price */}
                    <CentInput
                        value={popupValue.customPriceCents}
                        onChange={(ev) => {
                            setFieldAndPopupValue('customPriceCents', ev.target.value);
                        }}
                        error={
                            popupValue.ticketOptionId &&
                            popupValue.ticketTypeId &&
                            Boolean(errors.price)
                        }
                        label={t('bookingitems.columns.price')}
                        fullWidth
                        disabled={limitedModification}
                    />
                </Grid2>
                <Grid2 item size={6}>
                    <TextField
                        label={t('bookingitems.columns.priceWithTaxAndFees')}
                        disabled
                        value={formatCentsToUsd(finalPrice)}
                        fullWidth
                    />
                </Grid2>
                <Grid2 item size={12} alignContent={'flex-end'}>
                    <Grid2 container justifyContent="flex-end" spacing={1} wrap="nowrap">
                        <Grid2 item>
                            {/* Notes section toggle */}
                            <Tooltip title={t('common.expandNotes')}>
                                <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                                    <InfoIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid2>
                        {!limitedModification && typeof popupValue?.oldId === 'number' && (
                            <Grid2>
                                <Tooltip title={t('common.delete')}>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            setFieldAndPopupValue(
                                                'shouldDelete',
                                                !popupValue.shouldDelete,
                                            )
                                        }
                                    >
                                        <DeleteIcon
                                            color={popupValue.shouldDelete ? 'error' : 'inherit'}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Grid2>
                        )}
                        {!limitedModification &&
                            typeof popupValue?.oldId !== 'number' &&
                            !popupValue?.isNew && (
                                <Grid2>
                                    <Tooltip title={t('common.delete')}>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                removeAddedTicket();
                                            }}
                                        >
                                            <DeleteIcon color={'error'} />
                                        </IconButton>
                                    </Tooltip>
                                </Grid2>
                            )}

                        {!limitedModification && popupValue?.isNew && (
                            <Grid2>
                                <Tooltip title={t('common.add')}>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            // just remove the isNew flag
                                            addAddedTicket({ ...popupValue, isNew: undefined });
                                        }}
                                    >
                                        <SaveIcon color="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </Grid2>
                        )}
                        {closePopup && (
                            <Grid2>
                                <Tooltip title={t('common.close')}>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            closePopup();
                                        }}
                                    >
                                        <CloseIcon color="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </Grid2>
                        )}
                    </Grid2>
                </Grid2>

                {/* Validation warnings */}
                {(validationWarnings.seatMismatch || validationWarnings.messages.length > 0) && (
                    <Grid2 item size={12}>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                            {validationWarnings.seatMismatch && (
                                <Alert severity="error" icon={<WarningIcon fontSize="inherit" />}>
                                    {t('bookingitems.validation.seatNotCompatible')}
                                </Alert>
                            )}
                            {validationWarnings.messages.map((message, index) => (
                                <Alert
                                    key={index}
                                    severity="warning"
                                    icon={<WarningIcon fontSize="inherit" />}
                                >
                                    {message}
                                </Alert>
                            ))}
                        </Stack>
                    </Grid2>
                )}
            </Grid2>
            <Stack spacing={2}>
                {/* Expanded section - Notes */}
                {expanded && (
                    <>
                        <Divider />
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label={t('bookingitems.columns.note')}
                                value={popupValue.note}
                                onChange={(ev) => setFieldAndPopupValue('note', ev.target.value)}
                                disabled={isSubmitting}
                                multiline
                                rows={2}
                            />
                            <TextField
                                fullWidth
                                label={t('bookingitems.columns.checkinNote')}
                                value={popupValue.checkinNote}
                                onChange={(ev) =>
                                    setFieldAndPopupValue('checkinNote', ev.target.value)
                                }
                                disabled={isSubmitting}
                                multiline
                                rows={2}
                            />
                        </Stack>
                    </>
                )}
            </Stack>
        </Box>
    );
}

export default OneTicketEdit;
